import { Autocomplete, Button, TextField } from "@mui/material";
import { Box } from "@mui/system";
import { useCallback, useEffect, useState } from "react";
import { GetApiMethod } from "../../Utils/ApiService";
import { DatePicker } from "@mui/x-date-pickers";
import { Dayjs } from "dayjs";

const PlayerFilter = ({
  currentTab,
  selectedPlayer,
  setSelectedPlayer,
  selectedUser,
  setSelectedUser,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
}: any) => {
  const [allPlayers, setAllPlayers] = useState<any[]>([]);
  const [users, setAllUsers] = useState<any[]>([]);
  const [playerLoading, setPlayerLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);

  const fetchPlayers = useCallback(async (search: string = "") => {
    try {
      setPlayerLoading(true);
      const response = await GetApiMethod(
        `user?page=1&limit=10&sort=first_name&sortDirection=ASC&search=${encodeURIComponent(
          search
        )}&playerOnly=true`
      );
      const users = response.data.data.records;

      const formattedPlayers = users.map((player: any) => ({
        label: `${player.firstName} ${player.lastName}`,
        value: player.id,
      }));

      setAllPlayers(formattedPlayers);
    } catch (error) {
      console.error("Error fetching players:", error);
    } finally {
      setPlayerLoading(false);
    }
  }, []);

  const debouncedFetchPlayers = useCallback(
    (search: string = "") => {
      fetchPlayers(search);
    },
    [fetchPlayers]
  );

  const fetchAllUsers = useCallback(async (search: string = "") => {
    try {
      setUsersLoading(true);
      const response = await GetApiMethod(
        `user?page=1&limit=10&sort=first_name&sortDirection=ASC&search=${encodeURIComponent(
          search
        )}`
      );
      const users = response.data.data.records;

      const formattedUsers = users.map((user: any) => ({
        label: `${user.firstName} ${user.lastName}`,
        value: user.id,
      }));

      setAllUsers(formattedUsers);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setUsersLoading(false);
    }
  }, []);

  const debouncedFetchAllUsers = useCallback(
    (search: string = "") => {
      fetchAllUsers(search);
    },
    [fetchAllUsers]
  );

  useEffect(() => {
    fetchPlayers();
    fetchAllUsers();
  }, []);

  return (
    <Box
      display="flex"
      alignItems="center"
      gap={{ xs: 1, md: 2 }}
      mt={2}
      flexWrap="wrap"
      sx={{
        flexDirection: { xs: "column", md: "row" },
      }}
    >
      <Autocomplete
        disablePortal
        options={allPlayers}
        loading={playerLoading}
        sx={{
          width: { xs: "100%", md: "20%" },
        }}
        value={selectedPlayer}
        onChange={(e, value) => {
          setSelectedPlayer(value);
        }}
        renderInput={(params) => <TextField {...params} label="Players" />}
        onInputChange={(e, value) => {
          debouncedFetchPlayers(value);
        }}
        filterOptions={(x) => x}
        getOptionKey={(option) => option.value}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        size="small"
      />

      <Autocomplete
        disablePortal
        options={users}
        loading={usersLoading}
        sx={{
          width: { xs: "100%", md: "20%" },
        }}
        value={selectedUser}
        onChange={(e, value) => {
          setSelectedUser(value);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={
              currentTab === 0
                ? "Filter by Rejected By:"
                : "Filter by Accepted By:"
            }
          />
        )}
        onInputChange={(e, value) => {
          debouncedFetchAllUsers(value);
        }}
        getOptionKey={(option) => option.value}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        size="small"
      />

      <DatePicker
        label="Start Date"
        value={startDate}
        onChange={(date: Dayjs | null) => setStartDate(date)}
        slotProps={{
          field: {
            clearable: true,
            onClear: () => setStartDate(null),
          },
          textField: {
            size: "small",
            sx: { width: { xs: "100%", md: "20%" } },
          },
        }}
      />
      <DatePicker
        label="End Date"
        value={endDate}
        onChange={(date: Dayjs | null) => setEndDate(date)}
        slotProps={{
          field: {
            clearable: true,
            onClear: () => setEndDate(null),
          },
          textField: {
            size: "small",
            sx: { width: { xs: "100%", md: "20%" } },
          },
        }}
      />

      <Button
        variant="outlined"
        color="primary"
        onClick={() => {
          setSelectedPlayer("");
          setSelectedUser("");
          setStartDate(null);
          setEndDate(null);
        }}
      >
        Clear All
      </Button>
    </Box>
  );
};

export default PlayerFilter;
